import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { RenderIf } from '../utils/RenderIf';
import ModalBranchofficeSelect from '../NavBar/ModalBranchofficeSelect';
import { useSelectedBranch } from '../../../context/BranchContext';
import { TableSessionsFetchAPI } from '../../api/TableSessions';
import SuccessAlert from '../Alerts/SuccessAlert';
import ErrorAlert from '../Alerts/ErrorAlert';
import AlertSmall from '../Alerts/AlertSmall';
import Socket from '../utils/Socket.config';
import AppShell from '../../page/AppShell/AppShell';
import MainContent from '../MainContent/MainContent';

/* ─────────────────────────────────────────────────────────
   CSS — inyectado una vez. Prefijo: .tr-
   Reutiliza la misma paleta/variables que RoomsTables (.rt-)
   para mantener consistencia visual con el resto de Gestión.
───────────────────────────────────────────────────────── */
const TR_CSS = `
@keyframes tr-up { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
@keyframes tr-spin { to{transform:rotate(360deg)} }

.tr-page {
  min-height: 100vh;
  background: var(--bg-3, #F8F8F8);
  padding: 1.5rem clamp(1rem,3vw,2rem) 4rem;
}

/* ── banner ── */
.tr-banner {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem;
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px;
  padding: 1.1rem 1.35rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 10px rgba(0,0,0,.04);
}
.tr-banner-eyebrow {
  font-size: .68rem; font-weight: 700;
  letter-spacing: .09em; text-transform: uppercase;
  color: var(--brand, #F26522); margin-bottom: .22rem;
}
.tr-banner-title {
  font-size: 1.1rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  letter-spacing: -.02em; line-height: 1.2;
  display: flex; align-items: center; gap: .5rem; flex-wrap: wrap;
}
.tr-banner-branch { color: var(--brand, #F26522); }
.tr-banner-sub { font-size: .77rem; color: var(--text-3, #B4B4B4); margin-top: .2rem; }

/* ── no branch ── */
.tr-no-branch {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 280px; gap: 1rem;
  background: var(--bg, #fff);
  border: 1.5px dashed var(--border-md, rgba(0,0,0,.13));
  border-radius: 16px; padding: 2rem;
  text-align: center; animation: tr-up .35s ease both;
}
.tr-no-branch-icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: var(--brand-dim, rgba(242,101,34,.09));
  color: var(--brand, #F26522);
  display: grid; place-items: center; font-size: 1.6rem;
}
.tr-no-branch-title { font-size: .92rem; font-weight: 700; color: var(--text, #0A0A0A); }
.tr-no-branch-sub { font-size: .78rem; color: var(--text-3, #B4B4B4); max-width: 280px; line-height: 1.6; }

/* ── toolbar card ── */
.tr-card {
  background: var(--bg, #fff);
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,.04);
  animation: tr-up .3s ease both;
}
.tr-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: .75rem;
  padding: .9rem 1.15rem;
  border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
}
.tr-toolbar-left { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; }
.tr-card-title {
  font-size: .88rem; font-weight: 700;
  color: var(--text, #0A0A0A);
  font-family: var(--font-sans, -apple-system, sans-serif);
  display: flex; align-items: center; gap: .4rem;
}
.tr-count-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 18px; border-radius: 5px;
  background: var(--brand-dim, rgba(242,101,34,.09));
  color: var(--brand, #F26522);
  font-size: .65rem; font-weight: 700; padding: 0 .35rem;
}
.tr-toolbar-right { display: flex; align-items: center; gap: .55rem; flex-wrap: wrap; }

.tr-search {
  display: flex; align-items: center; gap: .45rem;
  padding: .4rem .8rem;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  border-radius: 999px;
  background: var(--bg-2, rgba(0,0,0,.03));
  transition: border-color .14s, box-shadow .14s;
  min-width: 200px;
}
.tr-search:focus-within {
  border-color: #1B3A6B;
  box-shadow: 0 0 0 3px rgba(27,58,107,.10);
  background: var(--bg, #fff);
}
.tr-search input { border: none; background: transparent; outline: none; font-size: .8rem; width: 100%; }

.tr-select {
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  border-radius: 999px;
  padding: .4rem .9rem;
  font-size: .78rem;
  background: var(--bg-2, rgba(0,0,0,.03));
  color: var(--text, #0A0A0A);
  outline: none;
}
.tr-select:focus { border-color: #1B3A6B; }

.tr-btn {
  display: inline-flex; align-items: center; gap: .35rem;
  border: 1.5px solid var(--border-md, rgba(0,0,0,.11));
  background: var(--bg, #fff);
  color: var(--text, #0A0A0A);
  border-radius: 999px; padding: .4rem .85rem;
  font-size: .78rem; font-weight: 600;
  cursor: pointer; transition: all .14s;
}
.tr-btn:hover:not(:disabled) { border-color: #1B3A6B; color: #1B3A6B; }
.tr-btn:disabled { opacity: .5; cursor: not-allowed; }
.tr-btn .tr-spin { animation: tr-spin .8s linear infinite; display: inline-block; }

.tr-btn-approve {
  border-color: rgba(22,140,54,.35); color: #168C36; background: rgba(22,140,54,.06);
}
.tr-btn-approve:hover:not(:disabled) { background: rgba(22,140,54,.14); border-color: #168C36; }
.tr-btn-reject {
  border-color: rgba(214,52,52,.3); color: #D63434; background: rgba(214,52,52,.06);
}
.tr-btn-reject:hover:not(:disabled) { background: rgba(214,52,52,.14); border-color: #D63434; }
.tr-btn-ghost { background: transparent; }

/* ── error banner ── */
.tr-error {
  display: flex; align-items: center; justify-content: space-between; gap: .75rem;
  flex-wrap: wrap;
  margin: 1rem 1.15rem 0;
  padding: .75rem 1rem;
  border-radius: 12px;
  background: rgba(214,52,52,.07);
  border: 1.5px solid rgba(214,52,52,.25);
  color: #B32626;
  font-size: .8rem;
}

/* ── table ── */
.tr-table-wrap { overflow-x: auto; }
.tr-table { width: 100%; border-collapse: collapse; font-size: .82rem; }
.tr-table thead th {
  text-align: left; font-size: .68rem; text-transform: uppercase; letter-spacing: .05em;
  color: var(--text-3, #B4B4B4); font-weight: 700;
  padding: .75rem 1.1rem; border-bottom: 1px solid var(--border, rgba(0,0,0,.07));
  white-space: nowrap;
}
.tr-table tbody td {
  padding: .8rem 1.1rem; border-bottom: 1px solid var(--border, rgba(0,0,0,.06));
  vertical-align: middle;
}
.tr-table tbody tr:last-child td { border-bottom: none; }
.tr-table tbody tr:hover { background: var(--bg-2, rgba(0,0,0,.015)); }
.tr-cell-primary { font-weight: 700; color: var(--text, #0A0A0A); }
.tr-cell-secondary { color: var(--text-3, #B4B4B4); font-size: .74rem; margin-top: .1rem; }
.tr-actions { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; }

/* ── status pill ── */
.tr-status-pill {
  display: inline-flex; align-items: center; gap: .3rem;
  padding: .25rem .6rem; border-radius: 999px;
  font-size: .66rem; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  white-space: nowrap;
}
.tr-status-PENDING  { background: rgba(242,178,34,.13); color: #B37B00; }
.tr-status-APPROVED { background: rgba(22,140,54,.12); color: #168C36; }
.tr-status-ACTIVE   { background: rgba(27,58,107,.10); color: #1B3A6B; }
.tr-status-REJECTED { background: rgba(214,52,52,.1); color: #D63434; }
.tr-status-EXPIRED  { background: rgba(0,0,0,.06); color: #6B6B6B; }

/* ── empty / loading ── */
.tr-empty {
  display: flex; flex-direction: column; align-items: center;
  padding: 3rem 1rem; text-align: center;
  animation: tr-up .3s ease both;
}
.tr-empty-icon {
  width: 58px; height: 58px; border-radius: 50%;
  background: var(--bg-2, rgba(0,0,0,.04));
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  display: grid; place-items: center;
  font-size: 1.5rem; margin-bottom: .7rem;
}
.tr-empty-title { font-size: .86rem; font-weight: 600; color: var(--text, #0A0A0A); margin: 0 0 .25rem; }
.tr-empty-sub { font-size: .76rem; color: var(--text-3, #B4B4B4); margin: 0; max-width: 340px; }

/* ── mobile cards ── */
.tr-mobile-grid { display: none; flex-direction: column; gap: .55rem; padding: .9rem; }
.tr-request-card {
  border: 1.5px solid var(--border, rgba(0,0,0,.07));
  border-radius: 12px; padding: .85rem .9rem; background: var(--bg, #fff);
}
.tr-request-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: .5rem; }

@media(max-width: 860px) {
  .tr-table-wrap { display: none; }
  .tr-mobile-grid { display: flex; }
}
`;

// Estados reales confirmados a partir del flujo de mesas del POS (Reservation.jsx /
// TableSessions.jsx: endpoints /approve y /reject, eventos de socket
// tableSessionApproved / tableSessionRejected / tableSessionActivated / tableSessionExpired).
// No se agregan estados que no tengan evidencia en el backend.
const STATUS_FILTERS = [
  { value: 'PENDING', label: 'Pendientes' },
  { value: 'APPROVED', label: 'Aprobadas' },
  { value: 'ACTIVE', label: 'Activas' },
  { value: 'REJECTED', label: 'Rechazadas' },
  { value: 'EXPIRED', label: 'Expiradas' },
];

const STATUS_LABEL = {
  PENDING: 'Pendiente',
  APPROVED: 'Aprobada',
  ACTIVE: 'Activa',
  REJECTED: 'Rechazada',
  EXPIRED: 'Expirada',
};

const REJECT_REASONS = ['Mesa no disponible', 'Solicitud duplicada', 'Cliente no localizado', 'Mesa incorrecta', 'Otro'];

//Function to normalize a raw table-session record from the backend into a flat, predictable shape
const normalizeSession = (raw = {}) => {
    const session = raw.session || raw.table_session || raw;
    const table = session.table || raw.table || {};
    const branchoffice = session.branchoffice || raw.branchoffice || {};
    const environment = session.environment || session.zone || table.environment || {};
    return {
        session_uuid: session.session_uuid || session.uuid || session.id || raw.session_uuid,
        table_id: session.table_id || table.id || raw.table_id,
        table_code: session.table_code || table.code || raw.table_code,
        table_name: session.table_name || table.name || raw.table_name,
        zone_name: table.environment_name || environment.name || session.zone_name || raw.zone_name,
        branchoffice_name: session.branchoffice_name || branchoffice.name || raw.branchoffice_name,
        branchoffice_code: session.branchoffice_code || branchoffice.code || raw.branchoffice_code,
        status: String(session.status || raw.status || '').toUpperCase(),
        source: session.source || raw.source,
        requested_at: session.requested_at || raw.requested_at || session.createdAt || raw.createdAt,
        approved_at: session.approved_at || raw.approved_at,
        client_name: session.client_name || session.customer_name || session.customer?.name || session.client?.name || raw.client_name,
        temp_order_code: session.temp_order_code || session.active_temp_order_code || session.order_code || raw.temp_order_code || raw.order_code,
    };
};

//Function to extract the list of sessions from the different response shapes the API may return
const extractList = (response) => {
    const data = response?.data?.data;
    const source = Array.isArray(data)
        ? data
        : (data?.items || response?.data?.items || (Array.isArray(response?.data) ? response.data : []));
    return (source || []).map(normalizeSession).filter((s) => s.session_uuid);
};

const getApiErrorMessage = (error, fallback = 'No se pudo completar la acción') => (
    error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
);

//Function to format a timestamp for display
const formatDateTime = (value) => {
    if (!value) return '—';
    const m = moment(value).tz('America/Bogota');
    return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '—';
};

//Function to get a human readable "elapsed time" label
const elapsedLabel = (value, nowTs) => {
    const date = value ? new Date(value).getTime() : null;
    if (!date || Number.isNaN(date)) return '—';
    const totalSeconds = Math.max(0, Math.floor((nowTs - date) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes < 1) return 'Hace unos segundos';
    if (minutes < 60) return `Hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Hace ${hours} h ${minutes % 60} min`;
    const days = Math.floor(hours / 24);
    return `Hace ${days} d`;
};

const TableRequests = () => {

    /* inject CSS once */
    useEffect(() => {
        const id = "tr-styles";
        if (!document.getElementById(id)) {
            const el = document.createElement("style");
            el.id = id; el.textContent = TR_CSS;
            document.head.appendChild(el);
        }
    }, []);

    let { business_code } = useParams();
    let user = JSON.parse(localStorage.getItem('user'));
    let token = localStorage.getItem('x-access-token');

    const { selectedBranch, setSelectedBranch } = useSelectedBranch();
    const [requests, setRequests] = useState([]);
    const [statusFilter, setStatusFilter] = useState('PENDING');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const [detailRequest, setDetailRequest] = useState(null);
    const [nowTs, setNowTs] = useState(Date.now());

    // Set con los session_uuid actualmente contados como PENDING. Es la única fuente
    // de verdad del contador, para que se mantenga correcto tanto con las respuestas
    // del backend como con los eventos de socket en tiempo real.
    const pendingIdsRef = useRef(new Set());
    // Ref del filtro activo, para que los listeners del socket (que se registran una
    // sola vez por sucursal) siempre lean el filtro más reciente y no uno "viejo"
    // capturado por el closure del efecto.
    const statusFilterRef = useRef(statusFilter);
    useEffect(() => { statusFilterRef.current = statusFilter; }, [statusFilter]);

    const syncPendingCountFromIds = () => setPendingCount(pendingIdsRef.current.size);

    //Timer used to keep "tiempo transcurrido" up to date without hitting the backend
    useEffect(() => {
        const id = setInterval(() => setNowTs(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    //Function to get the count of pending requests, independent of the status filter selected
    const fetchPendingCount = async (branchoffice_code) => {
        if (!branchoffice_code) { pendingIdsRef.current = new Set(); setPendingCount(0); return; }
        try {
            const response = await TableSessionsFetchAPI.getPendingSessions(
                { branchoffice_code, status: 'PENDING', limit: 100 },
                token
            );
            pendingIdsRef.current = new Set(extractList(response).map((s) => s.session_uuid));
            syncPendingCountFromIds();
        } catch (err) {
            //We don't surface this as a page error, the main list already handles that
        }
    };

    //Function to get the requests of the selected branch, filtered by status
    const fetchRequests = async (branchoffice_code, status) => {
        if (!branchoffice_code) return;
        setLoading(true);
        setError(null);
        try {
            const response = await TableSessionsFetchAPI.getPendingSessions(
                { branchoffice_code, status, limit: 100 },
                token
            );
            const list = extractList(response);
            setRequests(list);
            if (status === 'PENDING') {
                pendingIdsRef.current = new Set(list.map((s) => s.session_uuid));
                syncPendingCountFromIds();
            }
        } catch (err) {
            setRequests([]);
            setError(getApiErrorMessage(err, 'No se pudieron cargar las solicitudes. Intenta actualizar de nuevo.'));
        } finally {
            setLoading(false);
        }
    };

    const actionSelectBranch = (branchCode, branchInfo) => {
        setSelectedBranch({ code: branchCode, name: branchInfo?.branchoffice_name, id: branchInfo?.branchoffice_id });
        localStorage.setItem('branchoffice_selected', branchCode);
        fetchRequests(branchCode, statusFilter);
        fetchPendingCount(branchCode);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        if (selectedBranch.code) fetchRequests(selectedBranch.code, value);
    };

    const handleRefresh = () => {
        if (!selectedBranch.code) return;
        fetchRequests(selectedBranch.code, statusFilter);
        fetchPendingCount(selectedBranch.code);
    };

    //Function to approve a pending request
    const handleApprove = async (request) => {
        if (!request?.session_uuid || processingId) return;
        setProcessingId(request.session_uuid);
        try {
            await TableSessionsFetchAPI.approveSession(request.session_uuid, {}, token);
            setRequests((prev) => prev.filter((item) => item.session_uuid !== request.session_uuid));
            pendingIdsRef.current.delete(request.session_uuid);
            syncPendingCountFromIds();
            SuccessAlert("Solicitud aprobada correctamente");
        } catch (err) {
            if (err?.response?.status === 409) handleRefresh();
            ErrorAlert(getApiErrorMessage(err, 'No se pudo aprobar la solicitud'), "Solicitudes de mesa", "error");
        } finally {
            setProcessingId(null);
        }
    };

    //Function to reject a pending request, asking for confirmation and a reason first
    const handleReject = async (request) => {
        if (!request?.session_uuid || processingId) return;

        const { value: reason, isConfirmed } = await Swal.fire({
            title: '¿Rechazar esta solicitud?',
            html: `Mesa <b>${request.table_name || request.table_code || request.table_id}</b>. Esta acción no se puede revertir.`,
            icon: 'warning',
            input: 'select',
            inputOptions: REJECT_REASONS.reduce((acc, r) => ({ ...acc, [r]: r }), {}),
            inputPlaceholder: 'Selecciona un motivo',
            showCancelButton: true,
            confirmButtonColor: '#D63434',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, rechazar',
            cancelButtonText: 'Cancelar',
            inputValidator: (value) => !value && 'Selecciona un motivo para continuar',
        });

        if (!isConfirmed) return;

        let finalReason = reason;
        if (reason === 'Otro') {
            const { value: customReason } = await Swal.fire({
                title: 'Motivo del rechazo',
                input: 'text',
                inputPlaceholder: 'Describe el motivo',
                showCancelButton: true,
                confirmButtonText: 'Confirmar',
                cancelButtonText: 'Cancelar',
                inputValidator: (value) => !value && 'Ingresa un motivo',
            });
            if (!customReason) return;
            finalReason = customReason;
        }

        setProcessingId(request.session_uuid);
        try {
            await TableSessionsFetchAPI.rejectSession(request.session_uuid, { reason: finalReason }, token);
            setRequests((prev) => prev.filter((item) => item.session_uuid !== request.session_uuid));
            pendingIdsRef.current.delete(request.session_uuid);
            syncPendingCountFromIds();
            SuccessAlert("Solicitud rechazada");
        } catch (err) {
            if (err?.response?.status === 409) handleRefresh();
            ErrorAlert(getApiErrorMessage(err, 'No se pudo rechazar la solicitud'), "Solicitudes de mesa", "error");
        } finally {
            setProcessingId(null);
        }
    };

    // ── TIEMPO REAL ──────────────────────────────────────────────────────────
    // El backend ya emite eventos de socket para las solicitudes de mesa (se
    // confirmó revisando el flujo real del POS en Reservation.jsx, que se une a
    // la sala `business_{business_code}_branchoffice_{branchoffice_code}` y
    // escucha tableSessionRequested/Approved/Rejected/Activated/Closed/Expired/
    // Cancelled). Por eso esta pantalla se conecta por socket en vez de hacer
    // polling. La conexión es propia de este componente (mismo patrón que ya
    // usa ClientNotifications.jsx/Transactions.jsx: se crea al montar/():cambiar
    // de sucursal y se desconecta SOLO esa instancia al desmontar; no existe un
    // socket "global" compartido en el Dashboard, así que no hay riesgo de
    // desconectar algo de otro módulo.
    useEffect(() => {
        if (!business_code || !selectedBranch.code) return undefined;

        const socket = Socket({ business_code, branchoffice_code: selectedBranch.code });

        //We only react to events that belong to the currently active business/branchoffice room
        const payloadMatchesContext = (payload = {}) => {
            const session = payload.session || payload.table_session || payload;
            const payloadBranchCode = session.branchoffice_code || payload.branchoffice_code || session.branchoffice?.code || payload.branchoffice?.code;
            const payloadBusinessCode = session.business_code || payload.business_code || session.business?.code || payload.business?.code;
            if (payloadBranchCode && String(payloadBranchCode) !== String(selectedBranch.code)) return false;
            if (payloadBusinessCode && String(payloadBusinessCode) !== String(business_code)) return false;
            return true;
        };

        //A new access request was created from the ecommerce
        const handleNewRequest = (session) => {
            if (!pendingIdsRef.current.has(session.session_uuid)) {
                pendingIdsRef.current.add(session.session_uuid);
                syncPendingCountFromIds();
                AlertSmall({ message: `Nueva solicitud de mesa${session.table_name || session.table_code ? `: ${session.table_name || session.table_code}` : ''}`, icon: 'info' });
            }
            if (statusFilterRef.current === 'PENDING') {
                setRequests((prev) => [session, ...prev.filter((item) => item.session_uuid !== session.session_uuid)]);
            }
        };

        //The request left the pending pool (approved/rejected/expired/closed/cancelled), from the POS or elsewhere
        const handleTerminalEvent = (session) => {
            if (pendingIdsRef.current.has(session.session_uuid)) {
                pendingIdsRef.current.delete(session.session_uuid);
                syncPendingCountFromIds();
            }
            setRequests((prev) => {
                const withoutCurrent = prev.filter((item) => item.session_uuid !== session.session_uuid);
                return statusFilterRef.current === session.status ? [session, ...withoutCurrent] : withoutCurrent;
            });
        };

        //Secondary updates (activated, order updated) that don't change the pending pool
        const handleUpdateInPlace = (session) => {
            setRequests((prev) => {
                const existed = prev.some((item) => item.session_uuid === session.session_uuid);
                const withoutCurrent = prev.filter((item) => item.session_uuid !== session.session_uuid);
                if (statusFilterRef.current === session.status) return [session, ...withoutCurrent];
                return existed ? withoutCurrent : prev;
            });
        };

        const newRequestEvents = ['tableSessionRequested', 'newTableSessionRequest'];
        const terminalEvents = ['tableSessionApproved', 'tableSessionRejected', 'tableSessionExpired', 'tableSessionClosed', 'tableSessionCancelled'];
        const updateEvents = ['tableSessionActivated', 'tableSessionOrderUpdated'];
        const allEvents = [...newRequestEvents, ...terminalEvents, ...updateEvents];

        //We keep one handler reference per event so cleanup removes exactly what we added (avoids duplicated listeners)
        const boundHandlers = {};
        allEvents.forEach((eventName) => {
            const handler = (payload = {}) => {
                if (!payloadMatchesContext(payload)) return;
                const session = normalizeSession(payload.session || payload.table_session || payload);
                if (!session.session_uuid) return;

                if (newRequestEvents.includes(eventName)) return handleNewRequest(session);
                if (terminalEvents.includes(eventName)) return handleTerminalEvent(session);
                return handleUpdateInPlace(session);
            };
            boundHandlers[eventName] = handler;
            socket.on(eventName, handler);
        });

        return () => {
            const off = socket.off ? socket.off.bind(socket) : socket.removeListener?.bind(socket);
            allEvents.forEach((eventName) => off?.(eventName, boundHandlers[eventName]));
            socket.disconnect();
        };
    }, [business_code, selectedBranch.code]);

    const filteredRequests = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return requests;
        return requests.filter((r) => {
            const haystack = [
                r.table_name, r.table_code, r.client_name, r.session_uuid, r.branchoffice_name,
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(term);
        });
    }, [requests, search]);

    const isHotel = user.business.business_type.panamanianCode !== "9010";

    return (
        <AppShell route="tables">
            <MainContent>
                <div className="tr-page">

                    {/* ── BANNER ── */}
                    <div className="tr-banner">
                        <div>
                            <p className="tr-banner-eyebrow">Gestión</p>
                            <h1 className="tr-banner-title">
                                Solicitudes de mesa
                                {selectedBranch.name && (
                                    <span className="tr-banner-branch">— {selectedBranch.name}</span>
                                )}
                            </h1>
                            <p className="tr-banner-sub">
                                Gestión de solicitudes de acceso enviadas desde el ecommerce.
                            </p>
                        </div>
                        <ModalBranchofficeSelect actionSelectBranch={actionSelectBranch} />
                    </div>

                    {/* ── SIN SUCURSAL ── */}
                    <RenderIf isTrue={selectedBranch.code === 0}>
                        <div className="tr-no-branch">
                            <div className="tr-no-branch-icon"><i className="uil uil-clipboard-notes" /></div>
                            <p className="tr-no-branch-title">Ninguna sucursal seleccionada</p>
                            <p className="tr-no-branch-sub">Selecciona una sucursal para ver las solicitudes de acceso a mesas.</p>
                        </div>
                    </RenderIf>

                    {/* ── CONTENIDO ── */}
                    <RenderIf isTrue={selectedBranch.code !== 0}>
                        <div className="tr-card">
                            <div className="tr-toolbar">
                                <div className="tr-toolbar-left">
                                    <span className="tr-card-title">
                                        <i className="uil uil-clipboard-notes" /> Pendientes por atender
                                        <span className="tr-count-badge">{pendingCount}</span>
                                    </span>
                                </div>
                                <div className="tr-toolbar-right">
                                    <div className="tr-search">
                                        <i className="uil uil-search" />
                                        <input
                                            type="text"
                                            placeholder="Buscar por mesa, cliente o sesión..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <select
                                        className="tr-select"
                                        value={statusFilter}
                                        onChange={(e) => handleStatusFilterChange(e.target.value)}
                                    >
                                        {STATUS_FILTERS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <button className="tr-btn" onClick={handleRefresh} disabled={loading} title="Actualizar">
                                        <i className={`uil uil-sync ${loading ? 'tr-spin' : ''}`} /> Actualizar
                                    </button>
                                </div>
                            </div>

                            <RenderIf isTrue={!!error}>
                                <div className="tr-error">
                                    <span><i className="uil uil-exclamation-triangle" /> {error}</span>
                                    <button className="tr-btn" onClick={handleRefresh}>Reintentar</button>
                                </div>
                            </RenderIf>

                            <RenderIf isTrue={loading && filteredRequests.length === 0}>
                                <div className="tr-empty">
                                    <div className="tr-empty-icon"><i className="uil uil-sync tr-spin" /></div>
                                    <p className="tr-empty-title">Cargando solicitudes…</p>
                                </div>
                            </RenderIf>

                            <RenderIf isTrue={!loading && !error && filteredRequests.length === 0}>
                                <div className="tr-empty">
                                    <div className="tr-empty-icon">🔔</div>
                                    <p className="tr-empty-title">
                                        {search ? 'Sin resultados para tu búsqueda' : 'Sin solicitudes en este estado'}
                                    </p>
                                    <p className="tr-empty-sub">
                                        {search
                                            ? 'Prueba con otro término de búsqueda.'
                                            : 'Aquí aparecerán las solicitudes de acceso a mesas enviadas desde el ecommerce.'}
                                    </p>
                                </div>
                            </RenderIf>

                            <RenderIf isTrue={filteredRequests.length > 0}>
                                {/* ── TABLA (desktop) ── */}
                                <div className="tr-table-wrap">
                                    <table className="tr-table">
                                        <thead>
                                            <tr>
                                                <th>Mesa</th>
                                                <th>Cliente</th>
                                                <th>Sucursal</th>
                                                <th>Fecha y hora</th>
                                                <th>Tiempo transcurrido</th>
                                                <th>Estado</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredRequests.map((request) => {
                                                const isProcessing = processingId === request.session_uuid;
                                                const isPending = request.status === 'PENDING';
                                                return (
                                                    <tr key={request.session_uuid}>
                                                        <td className="tr-cell-primary">
                                                            {request.table_name || request.table_code || request.table_id}
                                                        </td>
                                                        <td>
                                                            {request.client_name || `Sesión ${String(request.session_uuid).slice(0, 8)}`}
                                                        </td>
                                                        <td>{request.branchoffice_name || selectedBranch.name || '—'}</td>
                                                        <td>{formatDateTime(request.requested_at)}</td>
                                                        <td className="tr-cell-secondary">{elapsedLabel(request.requested_at, nowTs)}</td>
                                                        <td>
                                                            <span className={`tr-status-pill tr-status-${request.status}`}>
                                                                {STATUS_LABEL[request.status] || request.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="tr-actions">
                                                                <RenderIf isTrue={isPending}>
                                                                    <button
                                                                        className="tr-btn tr-btn-approve"
                                                                        disabled={!!processingId}
                                                                        onClick={() => handleApprove(request)}
                                                                    >
                                                                        {isProcessing ? 'Procesando…' : 'Aprobar'}
                                                                    </button>
                                                                    <button
                                                                        className="tr-btn tr-btn-reject"
                                                                        disabled={!!processingId}
                                                                        onClick={() => handleReject(request)}
                                                                    >
                                                                        {isProcessing ? 'Procesando…' : 'Rechazar'}
                                                                    </button>
                                                                </RenderIf>
                                                                <button className="tr-btn tr-btn-ghost" onClick={() => setDetailRequest(request)}>
                                                                    Ver detalle
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* ── CARDS (mobile) ── */}
                                <div className="tr-mobile-grid">
                                    {filteredRequests.map((request) => {
                                        const isProcessing = processingId === request.session_uuid;
                                        const isPending = request.status === 'PENDING';
                                        return (
                                            <div className="tr-request-card" key={request.session_uuid}>
                                                <div className="tr-request-card-top">
                                                    <div>
                                                        <div className="tr-cell-primary">
                                                            {request.table_name || request.table_code || request.table_id}
                                                        </div>
                                                        <div className="tr-cell-secondary">
                                                            {request.client_name || `Sesión ${String(request.session_uuid).slice(0, 8)}`} · {elapsedLabel(request.requested_at, nowTs)}
                                                        </div>
                                                    </div>
                                                    <span className={`tr-status-pill tr-status-${request.status}`}>
                                                        {STATUS_LABEL[request.status] || request.status}
                                                    </span>
                                                </div>
                                                <div className="tr-actions" style={{ marginTop: '.65rem' }}>
                                                    <RenderIf isTrue={isPending}>
                                                        <button
                                                            className="tr-btn tr-btn-approve"
                                                            disabled={!!processingId}
                                                            onClick={() => handleApprove(request)}
                                                        >
                                                            {isProcessing ? 'Procesando…' : 'Aprobar'}
                                                        </button>
                                                        <button
                                                            className="tr-btn tr-btn-reject"
                                                            disabled={!!processingId}
                                                            onClick={() => handleReject(request)}
                                                        >
                                                            {isProcessing ? 'Procesando…' : 'Rechazar'}
                                                        </button>
                                                    </RenderIf>
                                                    <button className="tr-btn tr-btn-ghost" onClick={() => setDetailRequest(request)}>
                                                        Ver detalle
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </RenderIf>
                        </div>
                    </RenderIf>

                    {/* ── MODAL DETALLE ── */}
                    <Modal show={!!detailRequest} onHide={() => setDetailRequest(null)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Detalle de la solicitud</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <RenderIf isTrue={!!detailRequest}>
                                <p className="mb-1"><b>{isHotel ? 'Habitación' : 'Mesa'}:</b> {detailRequest?.table_name || detailRequest?.table_code || detailRequest?.table_id}</p>
                                <p className="mb-1"><b>Ambiente:</b> {detailRequest?.zone_name || 'No indicado'}</p>
                                <p className="mb-1"><b>Cliente:</b> {detailRequest?.client_name || 'Cliente sin nombre'}</p>
                                <p className="mb-1"><b>Origen:</b> {detailRequest?.source || 'QR'}</p>
                                <p className="mb-1"><b>Sucursal:</b> {detailRequest?.branchoffice_name || selectedBranch.name || '—'}</p>
                                <p className="mb-1"><b>Solicitada:</b> {formatDateTime(detailRequest?.requested_at)}</p>
                                <RenderIf isTrue={!!detailRequest?.approved_at}>
                                    <p className="mb-1"><b>Aprobada:</b> {formatDateTime(detailRequest?.approved_at)}</p>
                                </RenderIf>
                                <RenderIf isTrue={!!detailRequest?.temp_order_code}>
                                    <p className="mb-1"><b>Orden asociada:</b> {detailRequest?.temp_order_code}</p>
                                </RenderIf>
                                <p className="mb-1"><b>Estado:</b> {STATUS_LABEL[detailRequest?.status] || detailRequest?.status}</p>
                                <p className="mb-0 text-muted" style={{ fontSize: '.75rem' }}>ID de sesión: {detailRequest?.session_uuid}</p>
                            </RenderIf>
                        </Modal.Body>
                    </Modal>

                </div>
            </MainContent>
        </AppShell>
    );
};

export default TableRequests;
